// The ropes of `NinjaRopeGraphicsMan` (NinjaRopeGraphicsMan.cpp; docs/platform/RenderFidelity.md
// "Ropes"): the client half of `__CreateGFX` 0xd31700 and `__RenderRope` 0xd304c0. The sim
// records the point sets each `NinjaRopeGraphicsMan::GraphicUpdate` drew (crates/sim/src/view/
// ropes.rs, the frame view's rope tail); for each of the 16 rope specs this layer keeps what the
// original's spec holds: a strip shape in `FWDP_Transparent:WS_RenderGeometry_GameMID`
// (`Root.UI.UI_Trans_Emis`: the type's texture repeated, SRC_ALPHA/ONE_MINUS_SRC_ALPHA, depth test
// ALWAYS, no culling) with 1028 vertices that persist between frames, and the type's grapple mesh.
// Every frame each spec is drawn from its points or hidden, as `GraphicUpdate` does.
import * as THREE from 'three';
import { ktx2Texture } from '../../tools/viewer/ktx2.js';
import { assetBytes, assetJson } from '../assets.ts';
import { findMesh, instantiate, loadTemplate, type MeshInstance } from './meshes.ts';
import type { RopeView } from '../sim/view.ts';

/** `m_pRopeTextureNames` 0x1e490c0 and `m_pRopeXomNames` (the grapple meshes) 0x1e49100 by
 * `NinjaRopeGraphicsEnum` (NinjaRope, NinjaRopePro, NinjaRopeEasy, GoatTongue, TastyWormLick,
 * WormStinger; the In-Game bundle). */
const TEXTURES = ['WE_NinjaRope_01', 'WE_NinjaRope_03', 'WE_NinjaRope_05', 'WE_GoatTongueRope_01', 'WE_TastyWormLickRope_01', 'WE_WormStingerRope_01'];
const GRAPPLES = ['NinjaropeGrapple', 'NinjaropeGrapple_002', 'NinjaropeGrapple_003', 'WE_GoatToungueTip_01', '', ''];
const SPECS = 16;
/** `__CreateGFX`'s vertex sets: 0x3030 bytes of positions (257 quads of four vertices) and an
 * index set of 0x606 indices, per quad (s, s+1, s+2, s+1, s+3, s+2). */
const QUADS = 257;
const f = Math.fround;
/** `gRopeWidth` 0x1e49060 (the half width) and `gRopeUVWidth` 0x1e49064. */
const ROPE_WIDTH = f(5), ROPE_UV_WIDTH = f(10);
/** `fVScale`: 0.5 (0x1590164) × 1 / (gRopeUVWidth + gRopeUVWidth). */
const V_SCALE = f(f(0.5) * f(1 / f(ROPE_UV_WIDTH + ROPE_UV_WIDTH)));
/** `WormPoser::GetCurrentWormZOffset()` 0xc10ab0: 4.32f (0x15b7c08), the rope's z at the worm. */
const WORM_Z = f(4.32);
/** 1e-9f (0x159af20) and 1e-18f (0x15aa8a8). */
const EPS = f(1e-9), EPS_SQ = f(1e-18);

/** `NinjaRopeGraphicsMan::GetRopeType(uint32)` 0xd316e0: specs 0..3 are the single types, then
 * six specs per multi type. */
export function ropeType(spec: number): number {
  return spec > 3 ? Math.floor((spec - 4) / 6) + 4 : spec;
}

const VERT = /* glsl */ `
out vec2 slTexCoord1;
void main() {
  slTexCoord1 = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

// Root.UI.UI_Trans_Emis.Frag: the texture as it is.
const FRAG = /* glsl */ `
precision highp float;
uniform sampler2D Emissive1;
in vec2 slTexCoord1;
void main() {
  gl_FragColor = texture2D(Emissive1, slTexCoord1.xy);
}`;

type Spec = {
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>;
  pos: THREE.BufferAttribute;
  uv: THREE.BufferAttribute;
  grapple: { name: string; inst: MeshInstance | null } | null;
};

export class RopeLayer {
  readonly group = new THREE.Group();
  private specs: Spec[] = [];
  private textures = new Map<number, Promise<THREE.Texture | null>>();
  private renderer: THREE.WebGLRenderer | null = null;
  theme: string | null = null;

  constructor() {
    const index = new Uint16Array(QUADS * 6);
    for (let q = 0, s = 0; q < QUADS; q++, s += 4) index.set([s, s + 1, s + 2, s + 1, s + 3, s + 2], q * 6);
    for (let i = 0; i < SPECS; i++) {
      const geometry = new THREE.BufferGeometry();
      const pos = new THREE.BufferAttribute(new Float32Array(QUADS * 4 * 3), 3);
      // `__CreateGFX`: every quad's UVs (1, 1), (0, 1), (1, 0), (0, 0), positions 0.
      const uvs = new Float32Array(QUADS * 4 * 2);
      for (let q = 0; q < QUADS; q++) uvs.set([1, 1, 0, 1, 1, 0, 0, 0], q * 8);
      const uv = new THREE.BufferAttribute(uvs, 2);
      pos.setUsage(THREE.DynamicDrawUsage);
      uv.setUsage(THREE.DynamicDrawUsage);
      geometry.setAttribute('position', pos);
      geometry.setAttribute('uv', uv);
      geometry.setIndex(new THREE.BufferAttribute(index, 1));
      geometry.setDrawRange(0, 0);
      const material = new THREE.ShaderMaterial({
        vertexShader: VERT,
        fragmentShader: FRAG,
        uniforms: { Emissive1: { value: null } },
        // XBlendModeGL (source 6 SRC_ALPHA, dest ONE_MINUS_SRC_ALPHA), XDepthTest (enabled, 7 =
        // kCompareFunctionAlways: no test, depth still written), XCullFace off.
        transparent: true,
        blending: THREE.CustomBlending,
        blendEquation: THREE.AddEquation, blendSrc: THREE.SrcAlphaFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
        depthTest: true, depthFunc: THREE.AlwaysDepth, depthWrite: true,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.frustumCulled = false;
      mesh.visible = false;
      // Sorted with the worms and meshes of `WS_RenderGeometry_GameMID` (`Sort(State,
      // BackToFront)`: transparent shapes back to front by the view z of their bounds centre,
      // `XThreadedCullSortAction::AddShape` 0x1226350, `XGLBatchRenderer_Sorter_BF`). The rope's
      // XPsShape keeps the bounds every XNode is created with (`XBoundSphere::Empty`: centre
      // (0, 0, 0)), as nothing updates a dynamic node's bounds per frame, and its vertices are in
      // world space: it sorts as a shape at z 0, after a worm's body behind it (other worms, z
      // about -2.1) and before the current worm's body (z 0.09) and every worm's near hand.
      geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), -1);
      this.group.add(mesh);
      this.specs.push({ mesh, pos, uv, grapple: null });
    }
  }

  /** The renderer the rope textures (KTX2, S3TC) are made for. `__CreateGFX` 0xd31700 creates the
   * six textures when the manager starts, so they load here, not when a rope first appears. */
  setRenderer(renderer: THREE.WebGLRenderer): void {
    this.renderer = renderer;
    for (let t = 0; t < TEXTURES.length; t++) void this.texture(t);
  }

  /** The texture of rope type `type`: its image repeating in S and T (`__CreateGFX` sets
   * `kAddressModeRepeat` on the XOglTextureMap), linear, without mip filtering. */
  private texture(type: number): Promise<THREE.Texture | null> {
    let p = this.textures.get(type);
    if (!p) {
      p = (async () => {
        const name = TEXTURES[type];
        if (!name || !this.renderer) return null;
        type Graph = { graph: { objects: Record<string, { class: string; ktx2?: string | null }> } };
        const desc = await assetJson<Graph>(`xom/DataLinux/Bundles/In-Game/${name}.json`);
        const img = Object.values(desc.graph.objects).find(o => o.class === 'XImage' && o.ktx2);
        if (!img?.ktx2) return null;
        const { texture } = ktx2Texture(THREE, this.renderer, await assetBytes(img.ktx2));
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = false;
        texture.colorSpace = THREE.NoColorSpace;
        texture.needsUpdate = true;
        return texture as THREE.Texture;
      })().catch(e => { console.warn('rope texture', type, e); return null; });
      this.textures.set(type, p);
    }
    return p;
  }

  /** The grapple mesh of spec `s` (`GRM::CreateInstance(m_pRopeXomNames[type])`), loaded on first
   * use; null while it loads or for a type without one. */
  private grapple(s: Spec, type: number): MeshInstance | null {
    const name = GRAPPLES[type] ?? '';
    if (!name) return null;
    if (!s.grapple || s.grapple.name !== name) {
      s.grapple?.inst?.root.removeFromParent();
      const entry: { name: string; inst: MeshInstance | null } = { name, inst: null };
      s.grapple = entry;
      const found = findMesh(name, null, this.theme);
      if (found) void loadTemplate(found.glb, found.wanim).then(t => {
        if (!t || s.grapple !== entry) return;
        entry.inst = instantiate(t);
        entry.inst.root.visible = false;
        this.group.add(entry.inst.root);
      });
    }
    return s.grapple.inst;
  }

  /** `NinjaRopeGraphicsMan::GraphicUpdate`: every spec drawn from the frame's points or hidden. */
  update(ropes: RopeView[]): void {
    const bySpec = new Map<number, Float32Array>();
    for (const r of ropes) if (r.spec < SPECS) bySpec.set(r.spec, r.points);
    this.specs.forEach((s, i) => {
      const points = bySpec.get(i);
      const type = ropeType(i);
      if (points) {
        const mat = s.mesh.material;
        if (!mat.uniforms.Emissive1.value) void this.texture(type).then(t => { mat.uniforms.Emissive1.value = t; });
      }
      this.render(s, type, points ?? null);
    });
  }

  /** `__RenderRope` 0xd304c0 (from the disassembly), every float operation in float32 and in the
   * original's order. */
  private render(s: Spec, type: number, pts: Float32Array | null): void {
    const n = pts ? pts.length >> 1 : 0;
    const grapple = this.grapple(s, type);
    const hide = () => {
      s.mesh.visible = false;
      if (grapple) grapple.root.visible = false;
    };
    if (!pts || n <= 1) return hide();
    const segs = n - 1;
    s.mesh.visible = true;
    if (grapple) grapple.root.visible = true;
    const coords = s.pos.array as Float32Array, uvs = s.uv.array as Float32Array;
    let prim = 2 * segs;
    let saveX = 0, saveY = 0, saveZ = 0, prevDrawn = 0, rightX = 0, rightY = 0, start = 0, fV = 0;
    let drawn = 0, addons = 0, prevX = 0, prevY = 0, prevZ = 0, idx = 0, p2x = 0, p2y = 0;
    for (;;) {
      const idx1 = idx + 1;
      let next = idx1;
      // The second point: the next one, skipping points within 1e-9 of the first, but never
      // beyond the last.
      let p2i = idx1;
      if (segs > idx1) {
        const sx = pts[2 * start], sy = pts[2 * start + 1];
        const dist = (k: number) => { const dx = f(pts[2 * k] - sx), dy = f(pts[2 * k + 1] - sy); return f(f(dx * dx) + f(dy * dy)); };
        let d = dist(idx1);
        if (!(EPS_SQ < d)) {
          for (let k = idx1 + 1; ; k++) {
            p2i = k;
            if (k >= segs) break;
            d = dist(k);
            if (!(EPS_SQ >= d)) break;
          }
        }
      }
      // Legacy (x, y) → world (x, -y): `LegacyLandscape::LegacyToWorld`.
      const p1x = pts[2 * start], p1y = -pts[2 * start + 1], qx = pts[2 * p2i], qy = -pts[2 * p2i + 1];
      const dx = f(qx - p1x), dy = f(qy - p1y);
      const lenSq = f(f(dx * dx) + f(dy * dy));
      const segLen = f(Math.sqrt(lenSq));
      if (!(segLen > EPS)) {
        idx = idx1;
        start = next;
        if (segs <= next) break;
        continue;
      }
      let cx = dx, cy = dy, cz = 0;
      const lenSq0 = f(lenSq + 0);
      if (EPS < lenSq0 || Number.isNaN(lenSq0)) {
        const inv = f(1 / f(Math.sqrt(lenSq0)));
        cx = f(dx * inv); cy = f(dy * inv); cz = f(inv * 0);
      }
      const dot = f(f(f(prevY * cy) + f(prevX * cx)) + f(prevZ * cz));
      let addon = false, r14: number;
      if (dot < 0 && addons <= 4) {
        // A turn of more than 90°: an extra quad at the corner with the previous direction, then
        // the segment again from the same point.
        prim += 2;
        saveZ = cz; saveY = cy; saveX = cx;
        addons++;
        cz = prevZ; cy = prevY; cx = prevX;
        idx = prevDrawn;
        r14 = prevDrawn + 1;
        next = prevDrawn + 1;
        addon = true;
      } else r14 = idx1;
      // The middle of the previous and the current direction (the current alone at the start).
      const plen = f(f(f(prevY * prevY) + f(prevX * prevX)) + f(prevZ * prevZ));
      const keep = !(plen < EPS);
      const sx = keep ? prevX : cx, sy = keep ? prevY : cy, sz = keep ? prevZ : cz;
      let mz = f(f(sz + cz) * 0.5), my = f(f(sy + cy) * 0.5), mx = f(f(sx + cx) * 0.5);
      const mlenSq = f(f(f(mx * mx) + f(my * my)) + f(mz * mz));
      const mlen = f(Math.sqrt(mlenSq));
      if (mlen > EPS && !(EPS >= mlenSq)) {
        const inv = f(1 / mlen);
        my = f(my * inv); mx = f(mx * inv); mz = f(mz * inv);
      }
      // The right vector: middle × Z, times the half width.
      const t = f(mz * 0);
      rightX = f(f(my - t) * ROPE_WIDTH);
      rightY = f(f(t - mx) * ROPE_WIDTH);
      const ax = f(p1x + rightX), ay = f(p1y + rightY), by = f(p1y - rightY), bx = f(p1x - rightX);
      const z = f(f(f(f(idx) / f(segs)) * WORM_Z) + 0);
      let base = 0;
      if (drawn !== 0) {
        // The previous quad's end vertices.
        base = 4 * drawn;
        coords.set([ax, ay, z, bx, by, z], (base - 2) * 3);
        uvs.set([1, fV, 0, fV], (base - 2) * 2);
      }
      coords.set([ax, ay, z, bx, by, z], base * 3);
      uvs.set([1, fV, 0, fV], base * 2);
      if (drawn === 0 && grapple) {
        grapple.root.position.set(p1x, p1y, 0);
        grapple.root.rotation.set(0, 0, f(Math.atan2(mx, -my)));
        grapple.root.updateMatrixWorld(true);
      }
      drawn++;
      if (addon) {
        prevZ = saveZ; prevY = saveY; prevX = saveX;
      } else {
        fV = f(f(segLen * V_SCALE) + fV);
        prevZ = cz; prevY = cy; prevX = cx;
      }
      const oldIdx = idx;
      start = next;
      idx = r14;
      prevDrawn = oldIdx;
      p2x = qx; p2y = qy;
      if (!(segs > next)) break;
    }
    if (drawn === 0) return hide();
    const end = 4 * drawn;
    coords.set([f(rightX + p2x), f(rightY + p2y), WORM_Z, f(p2x - rightX), f(p2y - rightY), WORM_Z], (end - 2) * 3);
    uvs.set([1, fV, 0, fV], (end - 2) * 2);
    s.pos.needsUpdate = true;
    s.uv.needsUpdate = true;
    s.mesh.geometry.setDrawRange(0, prim * 3);
  }
}
